import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useGallery from '@/src/GalleryConfiguration/components/hooks/useGallery';
import useGetGalleries from '@/src/GalleryConfiguration/components/hooks/useGetGalleries';
import useActivation from '@/src/Activations/components/hooks/useActivation';
import useShare from '@/src/Share/components/hooks/useShare';
import useWhitelabelSettings from '@/src/WhitelabelSettings/components/hooks/useWhitelabelSettings';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';
import { Switch } from '@/src/shadComponents/ui/switch';
import AssetPicker from '../components/AssetPicker';
import useCrowdGallery from '@/src/GalleryConfiguration/components/hooks/useCrowdGallery';
import useFTPSettings from '@/src/GalleryConfiguration/components/hooks/useFTPSettings';
import usePicTime from '@/src/Orgs/components/hooks/usePicTime';

/*
 * Gallery v2 — the gallery hub (subject-detail archetype): left pane is
 * the gallery's identity (name, stats, links), right pane its settings
 * (basics, appearance, forwarding, tracking) saved as one object via
 * the classic PUT /galleries/:id. Asset uploads stay in classic for
 * now — this page links there for replacements.
 */

const IconExternal = ({ className = 'h-[15px] w-[15px]' }) => (
    <svg
        viewBox="0 0 20 20"
        className={className}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M8 4H5a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-3M12 4h4v4M16 4l-7 7" />
    </svg>
);

const IconCopy = () => (
    <svg
        viewBox="0 0 20 20"
        className="h-[13px] w-[13px]"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <rect x="7" y="7" width="9" height="9" rx="2" />
        <path d="M13 7V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" />
    </svg>
);

interface GalleryLink {
    title: string;
    path: string;
}

const GALLERY_LINKS: GalleryLink[] = [
    { title: 'Public gallery', path: '' },
    { title: 'Sharing station', path: '/sharing' },
    { title: 'Slideshow', path: '/slideshow' },
];

export default function GalleryV2() {
    const navigate = useNavigate();
    const { activationId } = useParams();
    const id = activationId ? +activationId : 0;
    const { getGallery, saveGallery } = useGallery();
    const { getGalleriesForActivation } = useGetGalleries();
    const { getActivationSessions } = useActivation();
    const { getSharesForActivation } = useShare();
    const { getOrgDomain } = useWhitelabelSettings();
    const { org } = useUserOrg();
    const { getCrowdGallery, saveCrowdGallery } = useCrowdGallery();
    const { getFTPSettings, saveFTPSettings, saveFTPSettingsWithPassword } =
        useFTPSettings();
    const { isLinkedToPictime } = usePicTime();

    const [gallery, setGallery] = useState<any>(null);
    const [form, setForm] = useState<any>(null);
    const [stats, setStats] = useState({ views: 0, shares: 0, photos: 0 });
    const [galleryUrl, setGalleryUrl] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [savedAt, setSavedAt] = useState<number | null>(null);
    const [saveError, setSaveError] = useState(false);
    const [copied, setCopied] = useState<string | null>(null);
    const [assetPicker, setAssetPicker] = useState<string | null>(null);
    const [pictimeLinked, setPictimeLinked] = useState(false);
    const [crowd, setCrowd] = useState<any>(null);
    const [crowdForm, setCrowdForm] = useState<any>(null);
    const [crowdSnapshot, setCrowdSnapshot] = useState('');
    const [crowdSaving, setCrowdSaving] = useState(false);
    const [ftpForm, setFtpForm] = useState<any>(null);
    const [ftpSnapshot, setFtpSnapshot] = useState('');
    const [ftpSaving, setFtpSaving] = useState(false);
    const [sectionError, setSectionError] = useState<string | null>(null);

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            try {
                const [galleries, sessions, shares, base] = await Promise.all([
                    getGalleriesForActivation(id),
                    getActivationSessions(id),
                    getSharesForActivation(id),
                    getOrgDomain(org?.id),
                ]);
                if (!mounted) return;
                setGalleryUrl(base ?? '');

                const seenSessions = new Set();
                const photos = (sessions ?? [])
                    .filter((s: any) => {
                        const k = s.photoSessionId ?? s.id;
                        if (seenSessions.has(k)) return false;
                        seenSessions.add(k);
                        return true;
                    })
                    .reduce(
                        (sum: number, s: any) => sum + (Number(s.total) || 0),
                        0,
                    );
                const shareIds = new Set(
                    (shares ?? []).map((s: any) => s.id),
                );

                const first = galleries?.[0];
                if (first) {
                    const [full, crowdRow, ftpRow, linked] =
                        await Promise.all([
                            getGallery(first.id),
                            getCrowdGallery(first.id).catch(() => null),
                            getFTPSettings(first.id).catch(() => null),
                            isLinkedToPictime().catch(() => false),
                        ]);
                    if (!mounted) return;
                    setGallery(full);
                    setForm(fromGallery(full));
                    setPictimeLinked(!!linked);
                    if (crowdRow) {
                        setCrowd(crowdRow);
                        const cf = {
                            enabled: !!crowdRow.enabled,
                            keyword: crowdRow.keyword ?? '',
                            welcomeMessage: crowdRow.welcomeMessage ?? '',
                            responseMessage: crowdRow.responseMessage ?? '',
                            webResponseMessage:
                                crowdRow.webResponseMessage ?? '',
                            moderationEnabled: !!crowdRow.moderationEnabled,
                            moderationNumber:
                                crowdRow.moderationNumber ?? '',
                            sendPhoto: !!crowdRow.sendPhoto,
                            sendPersonalGalleryLink:
                                !!crowdRow.sendPersonalGalleryLink,
                            sendGalleryLink: !!crowdRow.sendGalleryLink,
                        };
                        setCrowdForm(cf);
                        setCrowdSnapshot(JSON.stringify(cf));
                    }
                    if (ftpRow) {
                        const ff = {
                            ftpHost: ftpRow.ftpHost ?? '',
                            ftpPort: ftpRow.ftpPort ?? '',
                            ftpUser: ftpRow.ftpUser ?? '',
                            ftpDirectory: ftpRow.ftpDirectory ?? '',
                            ftpPassword: '',
                        };
                        setFtpForm(ff);
                        setFtpSnapshot(JSON.stringify(ff));
                    }
                    setStats({
                        views: Number(first.totalViews) || 0,
                        shares: shareIds.size,
                        photos,
                    });
                } else {
                    setStats({ views: 0, shares: shareIds.size, photos });
                }
            } catch (e) {
                /* empty state handles it */
            } finally {
                if (mounted) setIsLoading(false);
            }
        };
        load();
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const fromGallery = (g: any) => ({
        name: g.name ?? '',
        slug: g.slug ?? '',
        isPrivate: !!g.isPrivate,
        password: g.password ?? '',
        headerBackgroundColor: g.headerBackgroundColor ?? '',
        headerForegroundColor: g.headerForegroundColor ?? '',
        backgroundColor: g.backgroundColor ?? '',
        foregroundColor: g.foregroundColor ?? '',
        logo: g.logo ?? '',
        ctaImage: g.ctaImage ?? '',
        ctaText: g.ctaText ?? '',
        ctaLink: g.ctaLink ?? '',
        labelSearchPrompt: g.labelSearchPrompt ?? '',
        syncWithPictime: !!g.syncWithPictime,
        mfEnable: !!g.galleryMediaFilter?.enable,
        mfOriginal: !!g.galleryMediaFilter?.original,
        mfPhotos: !!g.galleryMediaFilter?.photos,
        mfVideos: !!g.galleryMediaFilter?.videos,
        mfEdited: !!g.galleryMediaFilter?.edited,
        mfPrints: !!g.galleryMediaFilter?.prints,
        forwardingEnabled: !!(g.galleryForwardUrl || g.shareForwardUrl),
        galleryForwardUrl: g.galleryForwardUrl ?? '',
        shareForwardUrl: g.shareForwardUrl ?? '',
        metaPixelId: g.metaPixelId ?? '',
    });

    const dirty = useMemo(() => {
        if (!gallery || !form) return false;
        return (
            JSON.stringify(form) !== JSON.stringify(fromGallery(gallery))
        );
    }, [gallery, form]);

    const set = (key: string, value: any) => {
        setForm((f: any) => ({ ...f, [key]: value }));
        setSaveError(false);
    };

    const save = async () => {
        if (!gallery || !form || isSaving) return;
        setIsSaving(true);
        setSaveError(false);
        const payload = {
            ...gallery,
            name: form.name,
            slug: form.slug,
            isPrivate: form.isPrivate,
            password: form.isPrivate ? form.password : '',
            headerBackgroundColor: form.headerBackgroundColor,
            headerForegroundColor: form.headerForegroundColor,
            backgroundColor: form.backgroundColor,
            foregroundColor: form.foregroundColor,
            logo: form.logo || null,
            ctaImage: form.ctaImage || null,
            ctaText: form.ctaText,
            ctaLink: form.ctaLink,
            labelSearchPrompt: form.labelSearchPrompt || null,
            syncWithPictime: form.syncWithPictime,
            galleryMediaFilter: {
                enable: form.mfEnable,
                original: form.mfOriginal,
                photos: form.mfPhotos,
                videos: form.mfVideos,
                edited: form.mfEdited,
                prints: form.mfPrints,
            },
            galleryForwardUrl: form.forwardingEnabled
                ? form.galleryForwardUrl || null
                : null,
            shareForwardUrl: form.forwardingEnabled
                ? form.shareForwardUrl || null
                : null,
            metaPixelId: form.metaPixelId,
        };
        const ok = await saveGallery(gallery.id, payload).catch(() => false);
        setIsSaving(false);
        if (ok) {
            setGallery(payload);
            setForm(fromGallery(payload));
            setSavedAt(Date.now());
        } else {
            setSaveError(true);
        }
    };

    const copy = (key: string, text: string) => {
        navigator.clipboard?.writeText(text).then(() => {
            setCopied(key);
            setTimeout(() => setCopied(null), 1500);
        });
    };

    const crowdDirty =
        crowdForm != null && JSON.stringify(crowdForm) !== crowdSnapshot;
    const ftpDirty =
        ftpForm != null && JSON.stringify(ftpForm) !== ftpSnapshot;

    const saveCrowd = async () => {
        if (!crowd || !crowdForm || crowdSaving) return;
        setCrowdSaving(true);
        setSectionError(null);
        const ok = await saveCrowdGallery(crowd.id, {
            ...crowdForm,
            galleryId: gallery.id,
        }).catch(() => false);
        setCrowdSaving(false);
        if (ok) {
            setCrowdSnapshot(JSON.stringify(crowdForm));
        } else {
            setSectionError('crowd');
        }
    };

    const saveFtp = async () => {
        if (!ftpForm || ftpSaving) return;
        setFtpSaving(true);
        setSectionError(null);
        const payload = { ...ftpForm, galleryId: gallery.id };
        const ok = await (ftpForm.ftpPassword
            ? saveFTPSettingsWithPassword(gallery.id, payload)
            : saveFTPSettings(gallery.id, payload)
        ).catch(() => false);
        setFtpSaving(false);
        if (ok) {
            setFtpSnapshot(
                JSON.stringify({ ...ftpForm, ftpPassword: '' }),
            );
            setFtpForm((f: any) => ({ ...f, ftpPassword: '' }));
        } else {
            setSectionError('ftp');
        }
    };

    const publicBase = gallery?.slug
        ? `${galleryUrl}/gallery/${gallery.slug}`
        : '';
    const displayUrl = (u: string) => u.replace(/^https?:\/\//, '');

    if (isLoading) {
        return (
            <main className="mx-auto max-w-[1020px] px-[clamp(18px,3vw,34px)] pt-4">
                {[0, 1, 2].map((i) => (
                    <div
                        key={i}
                        className="mb-4 h-16 animate-pulse rounded-[12px] bg-secondary"
                    />
                ))}
            </main>
        );
    }

    if (!gallery) {
        return (
            <main className="mx-auto max-w-[1020px] px-[clamp(18px,3vw,34px)] pt-4">
                <div className="ps-label mb-4">Gallery</div>
                <p className="text-[13.5px] text-muted-foreground">
                    This activation has no gallery yet.{' '}
                    <button
                        type="button"
                        onClick={() => navigate(`/activations/${id}/galleries`)}
                        className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] font-semibold text-brand-ink"
                    >
                        Create one in the classic app →
                    </button>
                </p>
            </main>
        );
    }

    return (
        <main className="mx-auto max-w-[1020px] px-[clamp(18px,3vw,34px)] pt-4">
            <div className="ps-label mb-[18px]">Gallery</div>
            <div className="grid gap-0 md:grid-cols-[352px_1fr]">
                {/* LEFT — the gallery hub */}
                <div className="md:pr-10">
                    <div className="mb-[22px] flex items-center gap-2.5">
                        <h1 className="text-[23px] font-bold leading-tight tracking-[-0.02em]">
                            {form.name || 'Untitled gallery'}
                        </h1>
                        {publicBase && (
                            <a
                                href={publicBase}
                                target="_blank"
                                rel="noreferrer"
                                title="View gallery"
                                className="flex h-7 w-7 items-center justify-center rounded-[8px] text-muted-foreground hover:bg-secondary hover:text-foreground"
                            >
                                <IconExternal />
                            </a>
                        )}
                    </div>

                    <div className="mb-1.5 flex gap-6">
                        {[
                            {
                                n: stats.views,
                                l: 'Views',
                                dot: 'bg-brand',
                            },
                            {
                                n: stats.shares,
                                l: 'Shares',
                                dot: 'bg-attention',
                            },
                            {
                                n: stats.photos,
                                l: 'Photos',
                                dot: 'bg-faint',
                            },
                        ].map((s) => (
                            <div key={s.l}>
                                <div className="text-[21px] font-bold tracking-[-0.01em] tabular-nums">
                                    {s.n.toLocaleString()}
                                </div>
                                <div className="mt-px flex items-center gap-1.5 text-[11.5px] text-muted-foreground">
                                    <span
                                        className={`h-1.5 w-1.5 rounded-full ${s.dot}`}
                                    />
                                    {s.l}
                                </div>
                            </div>
                        ))}
                    </div>
                    <button
                        type="button"
                        onClick={() => navigate(`/v2/activations/${id}/insights`)}
                        className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12.5px] font-semibold text-brand-ink"
                    >
                        View insights →
                    </button>

                    <div className="mt-7">
                        <div className="ps-label mb-1.5">Gallery links</div>
                        {GALLERY_LINKS.map((link, li) => {
                            const href = publicBase
                                ? `${publicBase}${link.path}`
                                : '';
                            return (
                                <div
                                    key={link.title}
                                    className={
                                        'flex items-center gap-2.5 py-[9px] ' +
                                        (li > 0 ? 'border-t border-line' : '')
                                    }
                                >
                                    <div className="min-w-0">
                                        <div className="text-[12.5px] font-semibold leading-tight">
                                            {link.title}
                                        </div>
                                        <div className="overflow-hidden text-ellipsis whitespace-nowrap font-mono text-[10.5px] text-faint">
                                            {href ? displayUrl(href) : '—'}
                                        </div>
                                    </div>
                                    {href && (
                                        <div className="ml-auto flex gap-0.5">
                                            <button
                                                type="button"
                                                title={
                                                    copied === link.title
                                                        ? 'Copied'
                                                        : 'Copy'
                                                }
                                                onClick={() =>
                                                    copy(link.title, href)
                                                }
                                                className={
                                                    'flex h-[26px] w-[26px] cursor-pointer items-center justify-center rounded-[7px] border-0 bg-transparent hover:bg-secondary hover:text-foreground ' +
                                                    (copied === link.title
                                                        ? 'text-good'
                                                        : 'text-faint')
                                                }
                                            >
                                                <IconCopy />
                                            </button>
                                            <a
                                                href={href}
                                                target="_blank"
                                                rel="noreferrer"
                                                title="Open"
                                                className="flex h-[26px] w-[26px] items-center justify-center rounded-[7px] text-faint hover:bg-secondary hover:text-foreground"
                                            >
                                                <IconExternal className="h-[13px] w-[13px]" />
                                            </a>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* RIGHT — settings */}
                <div className="mt-10 md:mt-0 md:border-l md:border-line md:pl-10">
                    <div className="mb-1.5 flex h-7 items-center justify-end gap-3">
                        {saveError && (
                            <span className="text-[11.5px] font-semibold text-attention">
                                Save failed — try again
                            </span>
                        )}
                        {dirty ? (
                            <button
                                type="button"
                                onClick={save}
                                disabled={isSaving}
                                className="cursor-pointer rounded-full border-0 bg-brand px-4 py-1.5 font-[inherit] text-[12px] font-semibold text-white"
                            >
                                {isSaving ? 'Saving…' : 'Save changes'}
                            </button>
                        ) : (
                            <span className="text-[11.5px] text-faint">
                                {savedAt ? 'Saved just now' : 'Saved'}
                            </span>
                        )}
                    </div>

                    {/* settings */}
                    <section className="pb-6 pt-3.5">
                        <h3 className="ps-label mb-[15px]">Settings</h3>
                        <div className="mb-[15px]">
                            <label className="mb-[7px] block text-[12px] font-semibold text-muted-foreground">
                                Gallery name
                            </label>
                            <input
                                className="ps-input w-full"
                                value={form.name}
                                onChange={(e) => set('name', e.target.value)}
                            />
                        </div>
                        <div className="mb-[15px]">
                            <label className="mb-[7px] block text-[12px] font-semibold text-muted-foreground">
                                Public address
                            </label>
                            <div className="flex items-baseline border-b-[1.5px] border-border focus-within:border-brand">
                                <span className="py-[9px] font-mono text-[12px] text-faint">
                                    {displayUrl(galleryUrl)}/gallery/
                                </span>
                                <input
                                    className="flex-1 border-0 bg-transparent py-[9px] font-mono text-[12.5px] text-foreground outline-none"
                                    value={form.slug}
                                    onChange={(e) =>
                                        set('slug', e.target.value)
                                    }
                                />
                            </div>
                        </div>
                        <div>
                            <label className="mb-[7px] block text-[12px] font-semibold text-muted-foreground">
                                Privacy
                            </label>
                            <div className="inline-flex gap-0.5 rounded-[9px] bg-secondary p-[3px]">
                                {[
                                    { label: 'Public', value: false },
                                    { label: 'Password', value: true },
                                ].map((o) => (
                                    <button
                                        key={o.label}
                                        type="button"
                                        onClick={() =>
                                            set('isPrivate', o.value)
                                        }
                                        className={
                                            'cursor-pointer rounded-[7px] border-0 px-[13px] py-[5px] font-[inherit] text-[12.5px] font-semibold ' +
                                            (form.isPrivate === o.value
                                                ? 'bg-background text-foreground'
                                                : 'bg-transparent text-muted-foreground')
                                        }
                                    >
                                        {o.label}
                                    </button>
                                ))}
                            </div>
                            {form.isPrivate && (
                                <div className="mt-[15px]">
                                    <label className="mb-[7px] block text-[12px] font-semibold text-muted-foreground">
                                        Password
                                    </label>
                                    <input
                                        type="password"
                                        autoComplete="new-password"
                                        className="ps-input w-full"
                                        value={form.password}
                                        onChange={(e) =>
                                            set('password', e.target.value)
                                        }
                                    />
                                </div>
                            )}
                        </div>
                    </section>

                    {/* appearance */}
                    <section className="border-t border-line py-6">
                        <h3 className="ps-label mb-1.5">Appearance</h3>
                        <p className="mb-4 text-[12.5px] text-muted-foreground">
                            How the gallery looks to guests.
                        </p>
                        <div className="mb-[15px]">
                            <label className="mb-[7px] block text-[12px] font-semibold text-muted-foreground">
                                Colors
                            </label>
                            <div className="flex flex-wrap gap-5">
                                {[
                                    {
                                        key: 'headerBackgroundColor',
                                        label: 'Header',
                                    },
                                    {
                                        key: 'headerForegroundColor',
                                        label: 'Header text',
                                    },
                                    {
                                        key: 'backgroundColor',
                                        label: 'Background',
                                    },
                                    {
                                        key: 'foregroundColor',
                                        label: 'Text',
                                    },
                                ].map((c) => (
                                    <div
                                        key={c.key}
                                        className="flex items-center gap-2"
                                    >
                                        <span
                                            className="h-6 w-6 rounded-[7px] shadow-[inset_0_0_0_1px_rgba(0,0,0,0.08)]"
                                            style={{
                                                background:
                                                    form[c.key] || '#ffffff',
                                            }}
                                        />
                                        <span>
                                            <span className="block text-[12px] font-semibold">
                                                {c.label}
                                            </span>
                                            <input
                                                className="w-[76px] border-0 bg-transparent p-0 font-mono text-[10.5px] uppercase text-faint outline-none"
                                                value={form[c.key]}
                                                placeholder="#——"
                                                onChange={(e) =>
                                                    set(c.key, e.target.value)
                                                }
                                            />
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div>
                            <label className="mb-[7px] block text-[12px] font-semibold text-muted-foreground">
                                Logo & call-to-action
                            </label>
                            <div className="flex flex-wrap gap-[22px]">
                                {[
                                    { key: 'logo', label: 'Logo' },
                                    { key: 'ctaImage', label: 'CTA' },
                                ].map((a) => (
                                    <div key={a.key}>
                                        <div className="flex h-14 w-[82px] items-center justify-center overflow-hidden rounded-[9px] bg-secondary">
                                            {form[a.key] ? (
                                                <img
                                                    src={form[a.key]}
                                                    alt={a.label}
                                                    className="h-full w-full object-contain"
                                                />
                                            ) : (
                                                <span className="text-[10px] text-faint">
                                                    None
                                                </span>
                                            )}
                                        </div>
                                        <div className="mt-1.5 text-[11px] text-muted-foreground">
                                            {a.label} ·{' '}
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    setAssetPicker(a.key)
                                                }
                                                className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[11px] font-semibold text-brand-ink"
                                            >
                                                Replace
                                            </button>
                                            {form[a.key] && (
                                                <>
                                                    {' · '}
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            set(a.key, '')
                                                        }
                                                        className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[11px] font-semibold text-faint hover:text-attention"
                                                    >
                                                        Remove
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>

                    {/* forwarding */}
                    <section className="border-t border-line py-6">
                        <h3 className="ps-label mb-[15px]">Forwarding</h3>
                        <div className="flex items-start justify-between gap-3.5">
                            <div>
                                <div className="text-[14px] font-semibold">
                                    Forward visitors to a custom URL
                                </div>
                                <div className="mt-1 max-w-[42ch] text-[12.5px] text-muted-foreground">
                                    Send gallery and share-link visitors to
                                    your own site instead.
                                </div>
                            </div>
                            <Switch
                                checked={form.forwardingEnabled}
                                onCheckedChange={(v) =>
                                    set('forwardingEnabled', v)
                                }
                            />
                        </div>
                        {form.forwardingEnabled && (
                            <>
                                <div className="mt-[15px]">
                                    <label className="mb-[7px] block text-[12px] font-semibold text-muted-foreground">
                                        Gallery URL
                                    </label>
                                    <input
                                        className="ps-input w-full font-mono text-[13px]"
                                        placeholder="https://"
                                        value={form.galleryForwardUrl}
                                        onChange={(e) =>
                                            set(
                                                'galleryForwardUrl',
                                                e.target.value,
                                            )
                                        }
                                    />
                                </div>
                                <div className="mt-[15px]">
                                    <label className="mb-[7px] block text-[12px] font-semibold text-muted-foreground">
                                        Share-link URL{' '}
                                        <span className="font-normal text-faint">
                                            — optional
                                        </span>
                                    </label>
                                    <input
                                        className="ps-input w-full font-mono text-[13px]"
                                        placeholder="Defaults to the gallery URL"
                                        value={form.shareForwardUrl}
                                        onChange={(e) =>
                                            set(
                                                'shareForwardUrl',
                                                e.target.value,
                                            )
                                        }
                                    />
                                </div>
                            </>
                        )}
                    </section>

                    {/* tracking */}
                    <section className="border-t border-line py-6">
                        <h3 className="ps-label mb-[15px]">Tracking</h3>
                        <div>
                            <label className="mb-[7px] block text-[12px] font-semibold text-muted-foreground">
                                Meta Pixel ID
                            </label>
                            <input
                                className="ps-input w-full font-mono text-[13px]"
                                value={form.metaPixelId}
                                onChange={(e) =>
                                    set('metaPixelId', e.target.value)
                                }
                            />
                        </div>
                    </section>

                    {/* content — what guests see in the gallery */}
                    <section className="border-t border-line py-6">
                        <h3 className="ps-label mb-[15px]">Content</h3>
                        <div className="flex items-start justify-between gap-3.5">
                            <div>
                                <div className="text-[14px] font-semibold">
                                    Filter what appears in the gallery
                                </div>
                                <div className="mt-1 max-w-[42ch] text-[12.5px] text-muted-foreground">
                                    When off, everything captured shows.
                                </div>
                            </div>
                            <Switch
                                checked={form.mfEnable}
                                onCheckedChange={(v) => set('mfEnable', v)}
                            />
                        </div>
                        {form.mfEnable && (
                            <div className="mt-3 flex flex-wrap gap-1.5">
                                {[
                                    { k: 'mfPhotos', l: 'Photos' },
                                    { k: 'mfVideos', l: 'Videos' },
                                    { k: 'mfEdited', l: 'Edited' },
                                    { k: 'mfPrints', l: 'Prints' },
                                    { k: 'mfOriginal', l: 'Originals' },
                                ].map((c) => (
                                    <button
                                        key={c.k}
                                        type="button"
                                        onClick={() => set(c.k, !form[c.k])}
                                        className={
                                            'cursor-pointer rounded-full px-3 py-[5px] font-[inherit] text-[12px] font-semibold ' +
                                            (form[c.k]
                                                ? 'border border-transparent bg-brand-soft text-brand-ink'
                                                : 'border border-border bg-transparent text-muted-foreground')
                                        }
                                    >
                                        {c.l}
                                    </button>
                                ))}
                            </div>
                        )}
                        <div className="mt-[15px]">
                            <label className="mb-[7px] block text-[12px] font-semibold text-muted-foreground">
                                Label search prompt{' '}
                                <span className="font-normal text-faint">
                                    — optional
                                </span>
                            </label>
                            <input
                                className="ps-input w-full"
                                value={form.labelSearchPrompt}
                                onChange={(e) =>
                                    set('labelSearchPrompt', e.target.value)
                                }
                            />
                        </div>
                        {pictimeLinked && (
                            <div className="mt-[15px] flex items-center justify-between gap-3.5">
                                <span className="text-[14px] font-semibold">
                                    Sync with Pic-Time
                                </span>
                                <Switch
                                    checked={form.syncWithPictime}
                                    onCheckedChange={(v) =>
                                        set('syncWithPictime', v)
                                    }
                                />
                            </div>
                        )}
                    </section>

                    {/* crowd gallery — separate save */}
                    {crowdForm && (
                        <section className="border-t border-line py-6">
                            <div className="mb-[15px] flex h-7 items-center justify-between">
                                <h3 className="ps-label">Crowd gallery</h3>
                                <span className="flex items-center gap-3">
                                    {sectionError === 'crowd' && (
                                        <span className="text-[11.5px] font-semibold text-attention">
                                            Save failed
                                        </span>
                                    )}
                                    {crowdDirty && (
                                        <button
                                            type="button"
                                            onClick={saveCrowd}
                                            disabled={crowdSaving}
                                            className="cursor-pointer rounded-full border-0 bg-brand px-4 py-1.5 font-[inherit] text-[12px] font-semibold text-white"
                                        >
                                            {crowdSaving
                                                ? 'Saving…'
                                                : 'Save changes'}
                                        </button>
                                    )}
                                </span>
                            </div>
                            <div className="flex items-start justify-between gap-3.5">
                                <div>
                                    <div className="text-[14px] font-semibold">
                                        Guests text photos in
                                    </div>
                                    <div className="mt-1 max-w-[42ch] text-[12.5px] text-muted-foreground">
                                        SMS keyword drops guest photos into
                                        the gallery.
                                    </div>
                                </div>
                                <Switch
                                    checked={crowdForm.enabled}
                                    onCheckedChange={(v) =>
                                        setCrowdForm((f: any) => ({
                                            ...f,
                                            enabled: v,
                                        }))
                                    }
                                />
                            </div>
                            {crowdForm.enabled && (
                                <>
                                    {[
                                        { k: 'keyword', l: 'Keyword', mono: true },
                                        {
                                            k: 'welcomeMessage',
                                            l: 'Welcome message',
                                        },
                                        {
                                            k: 'responseMessage',
                                            l: 'Response message',
                                        },
                                        {
                                            k: 'webResponseMessage',
                                            l: 'Web response message',
                                        },
                                    ].map((f2) => (
                                        <div key={f2.k} className="mt-[15px]">
                                            <label className="mb-[7px] block text-[12px] font-semibold text-muted-foreground">
                                                {f2.l}
                                            </label>
                                            <input
                                                className={
                                                    'ps-input w-full' +
                                                    (f2.mono
                                                        ? ' font-mono text-[13px]'
                                                        : '')
                                                }
                                                value={crowdForm[f2.k]}
                                                onChange={(e) =>
                                                    setCrowdForm(
                                                        (f: any) => ({
                                                            ...f,
                                                            [f2.k]:
                                                                e.target
                                                                    .value,
                                                        }),
                                                    )
                                                }
                                            />
                                        </div>
                                    ))}
                                    <div className="mt-3.5 flex flex-wrap gap-1.5">
                                        {[
                                            { k: 'sendPhoto', l: 'Send photo back' },
                                            {
                                                k: 'sendPersonalGalleryLink',
                                                l: 'Personal gallery link',
                                            },
                                            {
                                                k: 'sendGalleryLink',
                                                l: 'Gallery link',
                                            },
                                            {
                                                k: 'moderationEnabled',
                                                l: 'Moderation',
                                            },
                                        ].map((c) => (
                                            <button
                                                key={c.k}
                                                type="button"
                                                onClick={() =>
                                                    setCrowdForm(
                                                        (f: any) => ({
                                                            ...f,
                                                            [c.k]: !f[c.k],
                                                        }),
                                                    )
                                                }
                                                className={
                                                    'cursor-pointer rounded-full px-3 py-[5px] font-[inherit] text-[12px] font-semibold ' +
                                                    (crowdForm[c.k]
                                                        ? 'border border-transparent bg-brand-soft text-brand-ink'
                                                        : 'border border-border bg-transparent text-muted-foreground')
                                                }
                                            >
                                                {c.l}
                                            </button>
                                        ))}
                                    </div>
                                </>
                            )}
                        </section>
                    )}

                    {/* ftp sync — separate save */}
                    {ftpForm && (
                        <section className="border-t border-line py-6">
                            <div className="mb-[15px] flex h-7 items-center justify-between">
                                <h3 className="ps-label">FTP sync</h3>
                                <span className="flex items-center gap-3">
                                    {sectionError === 'ftp' && (
                                        <span className="text-[11.5px] font-semibold text-attention">
                                            Save failed
                                        </span>
                                    )}
                                    {ftpDirty && (
                                        <button
                                            type="button"
                                            onClick={saveFtp}
                                            disabled={ftpSaving}
                                            className="cursor-pointer rounded-full border-0 bg-brand px-4 py-1.5 font-[inherit] text-[12px] font-semibold text-white"
                                        >
                                            {ftpSaving
                                                ? 'Saving…'
                                                : 'Save changes'}
                                        </button>
                                    )}
                                </span>
                            </div>
                            <div className="grid gap-x-[22px] md:grid-cols-2">
                                {[
                                    { k: 'ftpHost', l: 'Host' },
                                    { k: 'ftpPort', l: 'Port' },
                                    { k: 'ftpUser', l: 'Username' },
                                    { k: 'ftpDirectory', l: 'Directory' },
                                ].map((f2) => (
                                    <div key={f2.k} className="mt-[15px] first:mt-0 md:[&:nth-child(2)]:mt-0">
                                        <label className="mb-[7px] block text-[12px] font-semibold text-muted-foreground">
                                            {f2.l}
                                        </label>
                                        <input
                                            className="ps-input w-full font-mono text-[13px]"
                                            value={ftpForm[f2.k]}
                                            onChange={(e) =>
                                                setFtpForm((f: any) => ({
                                                    ...f,
                                                    [f2.k]: e.target.value,
                                                }))
                                            }
                                        />
                                    </div>
                                ))}
                            </div>
                            <div className="mt-[15px]">
                                <label className="mb-[7px] block text-[12px] font-semibold text-muted-foreground">
                                    Password{' '}
                                    <span className="font-normal text-faint">
                                        — enter to change
                                    </span>
                                </label>
                                <input
                                    type="password"
                                    autoComplete="new-password"
                                    className="ps-input w-full font-mono text-[13px]"
                                    value={ftpForm.ftpPassword}
                                    onChange={(e) =>
                                        setFtpForm((f: any) => ({
                                            ...f,
                                            ftpPassword: e.target.value,
                                        }))
                                    }
                                />
                            </div>
                        </section>
                    )}
                </div>
            </div>
            {assetPicker && (
                <AssetPicker
                    onClose={() => setAssetPicker(null)}
                    onSelect={(url) => {
                        set(assetPicker, url);
                        setAssetPicker(null);
                    }}
                />
            )}
        </main>
    );
}
